'use client'

import type { ComponentType } from 'react'

import dynamic from 'next/dynamic'

import { FormattedMessage } from '@b-stock/fe-core/bstock-react'

import { DetailsValue } from '@/buyer-portal/components/features/shared'
import { HeaderAlignedPageWidth } from '@/components/PageWidth'
import type { CmsSellerProfile } from '@/helpers/api/fetchPayload'
import { signUpCtaLocationForTab } from '@/helpers/telemetry/signupLinkTracking'

import { useStorefrontId } from '../StorefrontContext'
import { lexicalHasContent } from './cmsSellerProfileAbout'
import { SellerProfileLexicalRichText } from './SellerProfileLexicalRichText'
import { Container, InfoSection, StorefrontTableContainer, Title } from './ui'

// Dynamic so a visitor only downloads the one seller body their storefront resolves to.
const BellConditionDetails = dynamic(() => import('./bellConditionDetails'))
const GmeConditionDetails = dynamic(() => import('./gmeConditionDetails'))
const MobiwiseConditionDetails = dynamic(
  () => import('./mobiwiseConditionDetails')
)
const SamConditionDetails = dynamic(() => import('./samConditionDetails'))
const SqtConditionDetails = dynamic(() => import('./sqtConditionDetails'))
const TmoConditionDetails = dynamic(() => import('./tmoConditionDetails'))

/**
 * Storefront ids are per environment, so a seller is listed once per environment it exists in.
 * Same key the LaunchDarkly `conditionsEnabledStorefrontIds` allowlist uses, which is what keeps
 * turning a seller on a single config change in one place.
 *
 * A storefront the allowlist turns the tab on for but that is absent here has no body of its own —
 * it must not borrow another seller's, so it renders the empty state.
 */
const LEGACY_CONDITION_BY_STOREFRONT_ID: Record<string, ComponentType> = {
  // T-Mobile — dev, staging, prod. No qa storefront exists for this seller.
  '67d41cd1f27ef0e825a2fdaf': TmoConditionDetails,
  '6a63f164fe43dbbe52e2b180': TmoConditionDetails,
  '6a863bc02f2e4d913d44fcfa': TmoConditionDetails,
  // SquareTrade Auctions — dev, staging, prod. No qa storefront exists for this seller.
  '6a91f33db9e1543ad2c72653': SqtConditionDetails,
  '6a88859c2e6c90c114454fb4': SqtConditionDetails,
  '6a8dfb274ecd6bcdfdf3e14c': SqtConditionDetails,
  // Samsung Mobile Auctions — dev, qa, staging, prod.
  '6a2669661c51b11f66cfd410': SamConditionDetails,
  '6a841d00997027b0012170d4': SamConditionDetails,
  '6aa2e27da76df973fab0e700': SamConditionDetails,
  '6aa46da9ae94c10d2b0ee5e7': SamConditionDetails,
  // Wireless Network Marketplace (Bell) — dev, staging, prod. No qa storefront exists.
  '67fe41738fb4fab5c366eb92': BellConditionDetails,
  '6a95bc62c5b1cb8ad5091652': BellConditionDetails,
  '6a99a1690ae82efdcd6f6ce1': BellConditionDetails,
  // GameStop — dev, qa, staging, prod.
  '66c5d7333901d41725ed1f5b': GmeConditionDetails,
  '66e26bce572574d053543311': GmeConditionDetails,
  '6aa19a25cde1ac3244c8be79': GmeConditionDetails,
  '6aa46be9265d0ee1f8aa5ae1': GmeConditionDetails,
  // Mobiwise — dev (sandbox test storefront), staging, prod.
  '6aa1ef6443c6ecd12cc6ac37': MobiwiseConditionDetails,
  '6a98815ba489cbdf2b3fe146': MobiwiseConditionDetails,
  '6a9881c4b1d836e27323e388': MobiwiseConditionDetails,
}

export default function ConditionContent({
  cmsSellerProfile,
}: {
  /** CMS row for this seller; its conditions body replaces the hardcoded content when filled in. */
  cmsSellerProfile?: CmsSellerProfile | null
}) {
  const storefrontId = useStorefrontId()
  const cms = cmsSellerProfile
  const showCms = Boolean(cms && lexicalHasContent(cms.conditionsBody))
  const LegacyDetails = storefrontId
    ? LEGACY_CONDITION_BY_STOREFRONT_ID[storefrontId]
    : undefined

  return (
    <HeaderAlignedPageWidth>
      <StorefrontTableContainer>
        <Title>
          <FormattedMessage id="Storefront.condition.title" />
        </Title>
        <InfoSection>
          <DetailsValue>
            {showCms && cms ? (
              <Container>
                <SellerProfileLexicalRichText
                  data={cms.conditionsBody}
                  signUpCtaLocation={signUpCtaLocationForTab(
                    cms.sellerSlug,
                    'condition'
                  )}
                />
              </Container>
            ) : LegacyDetails ? (
              <LegacyDetails />
            ) : (
              <Container>
                <p>
                  <FormattedMessage id="Storefront.condition.emptyCms" />
                </p>
              </Container>
            )}
          </DetailsValue>
        </InfoSection>
      </StorefrontTableContainer>
    </HeaderAlignedPageWidth>
  )
}
